import { useRef, useState } from "react"
import type { TypographyProps } from "@mui/material"
import { Box, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { useAppLocalization } from "../../../modules/localization/components/AppLocalizationProvider"
import { useInlineClampedText } from "../createOrder/components/drawer/messages/useInlineClampedText"
import { HIDDEN_IN_PRINT } from "../print/printPageOnly"

type StyleProps = Readonly<{
    maxLines: number
}>

const useStyles = makeStyles<StyleProps>()((theme, { maxLines }) => ({
    text: {
        whiteSpace: "pre-wrap",
        overflowWrap: "anywhere",
    },
    previewWrapper: HIDDEN_IN_PRINT,
    previewCollapsed: {
        display: "-webkit-box",
        WebkitLineClamp: maxLines,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
    },
    printOnly: {
        display: "none",
        "@media print": {
            display: "block",
        },
    },
    toggle: {
        fontWeight: 700,
        color: theme.palette.tailwind.blue[500],
        cursor: "pointer",
    },
    measurerRoot: {
        position: "absolute",
        visibility: "hidden",
        pointerEvents: "none",
        top: "-10000px",
        left: "-10000px",
    },
    measurerText: {
        whiteSpace: "pre-wrap",
        overflowWrap: "anywhere",
    },
}))

const ELLIPSIS = "…"

type ExpandableTextProps = Readonly<{
    text: string
    maxLines?: number
    variant?: TypographyProps["variant"]
    className?: string
}>

export function ExpandableText({ text, maxLines = 2, variant = "body2", className }: ExpandableTextProps) {
    const { classes, cx } = useStyles({ maxLines })
    const { l10n } = useAppLocalization()

    const [isExpanded, setIsExpanded] = useState(false)

    const observeSizeRef = useRef<HTMLDivElement | null>(null)
    const measureRef = useRef<HTMLParagraphElement | null>(null)
    const measurePrefixRef = useRef<HTMLSpanElement | null>(null)
    const measureSuffixRef = useRef<HTMLSpanElement | null>(null)

    const readMoreLabel = ` ${l10n.getString("expandable-text-read-more")}`
    const readLessLabel = ` ${l10n.getString("expandable-text-read-less")}`

    const { previewText, isTruncated } = useInlineClampedText({
        fullText: text,
        maxLines,
        suffixText: `${ELLIPSIS}${readMoreLabel}`,
        measureRef,
        measurePrefixRef,
        measureSuffixRef,
        observeSizeRef,
    })

    const toggle = () => setIsExpanded((prev) => !prev)

    return (
        <Box ref={observeSizeRef}>
            <Box className={classes.previewWrapper}>
                <Typography
                    variant={variant}
                    component="div"
                    className={cx(classes.text, !isExpanded && classes.previewCollapsed, className)}
                >
                    {isTruncated && !isExpanded ? `${previewText}${ELLIPSIS}` : text}
                    {isTruncated && (
                        <span
                            role="button"
                            tabIndex={0}
                            className={classes.toggle}
                            onClick={toggle}
                            onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault()
                                    toggle()
                                }
                            }}
                        >
                            {isExpanded ? readLessLabel : readMoreLabel}
                        </span>
                    )}
                </Typography>
            </Box>

            <Typography variant={variant} component="div" className={cx(classes.text, classes.printOnly, className)}>
                {text}
            </Typography>

            <Box className={classes.measurerRoot} aria-hidden>
                <Typography variant={variant} className={classes.measurerText} ref={measureRef}>
                    <span ref={measurePrefixRef} />
                    <span ref={measureSuffixRef} className={classes.toggle} />
                </Typography>
            </Box>
        </Box>
    )
}
